// Section 5: FSI & Construction Parameters
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { TrendingUp } from "lucide-react";
import { IndianNumberInput } from "@/components/IndianNumberInput";

export const FSIParametersSection = ({
  formData,
  setFormData,
  handleChange,
  handleSelectChange,
  getInputClass,
  hasFieldError,
  fsiBreakdown
}) => {
  const isNarrowRoad = fsiBreakdown?.isNarrowRoad || false;
  return (
    <Card className="rounded-sm shadow-sm border-l-4 border-l-violet-400" data-testid="quick-fsi-params">
      <CardHeader className="pb-3 bg-violet-100 rounded-t-xl border-b border-violet-200">
        <CardTitle className="font-heading tracking-tight text-lg flex items-center gap-2 text-violet-900">
          <TrendingUp className="h-5 w-5 text-muted-foreground" />
          5. FSI & Construction Parameters
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="q-loadingPercentage">Loading - Flats (%)</Label>
            <Input
              id="q-loadingPercentage"
              name="loadingPercentage"
              type="number"
              value={formData.loadingPercentage}
              onChange={handleChange}
              placeholder="40"
              className="rounded-sm"
              data-testid="quick-loading"
            />
          </div>
          {/* Loading for Offices - Only for Commercial or Mixed */}
          {(formData.projectType === "commercial" || formData.projectType === "mixed") && (
            <div className="space-y-2">
              <Label htmlFor="q-loadingPercentageOffices">Loading - Offices (%)</Label>
              <Input
                id="q-loadingPercentageOffices"
                name="loadingPercentageOffices"
                type="number"
                value={formData.loadingPercentageOffices || ""}
                onChange={handleChange}
                placeholder="45"
                className="rounded-sm"
                data-testid="quick-loading-offices"
              />
            </div>
          )}
          {/* Loading for Shops - Only for Commercial or Mixed */}
          {(formData.projectType === "commercial" || formData.projectType === "mixed") && (
            <div className="space-y-2">
              <Label htmlFor="q-loadingPercentageShops">Loading - Shops (%)</Label>
              <Input
                id="q-loadingPercentageShops"
                name="loadingPercentageShops"
                type="number"
                value={formData.loadingPercentageShops || ""}
                onChange={handleChange}
                placeholder="50"
                className="rounded-sm"
                data-testid="quick-loading-shops"
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="q-carpetAreaFactor">Carpet Area Factor (% of carpet over built-up area)</Label>
            <Input
              id="q-carpetAreaFactor"
              name="carpetAreaFactor"
              type="number"
              value={formData.carpetAreaFactor}
              onChange={handleChange}
              placeholder="70"
              className="rounded-sm"
              data-testid="quick-carpet-factor"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="q-constructionCostPerSqft">Construction Cost (₹/sft) *</Label>
            <IndianNumberInput
              id="q-constructionCostPerSqft"
              name="constructionCostPerSqft"
              value={formData.constructionCostPerSqft}
              onChange={handleChange}
              placeholder="2,800"
              className={getInputClass("constructionCostPerSqft")}
              data-testid="quick-construction-cost"
            />
            {hasFieldError("constructionCostPerSqft") && <p className="text-xs text-red-500">Required</p>}
          </div>
        </div>
        
        {/* Narrow Road Notice */}
        {isNarrowRoad && formData.projectClassification !== "mhada" && (
          <>
            <Separator />
            <div className="bg-amber-50 border border-amber-200 p-3 rounded-sm text-sm text-amber-700">
              <strong>Road Width &lt; 9m:</strong> TDR and Paid FSI are not applicable. Only Base FSI (1.1), 30% Incentive FSI, Green FSI, and Ancillary FSI apply.
            </div>
          </>
        )}

        {/* TDR Split & Purchase Price - Only show for Non-MHADA and Non-Narrow-Road */}
        {formData.projectClassification !== "mhada" && !isNarrowRoad && (
          <>
            <Separator />
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>TDR Split Ratio</Label>
                <Select
                  value={formData.tdrSplitRatio || "70:30"}
                  onValueChange={(value) => {
                    const [amenity, slum] = value.split(":").map(Number);
                    setFormData({
                      ...formData,
                      tdrSplitRatio: value,
                      tdrSplitAmenity: amenity,
                      tdrSplitSlum: slum,
                      // Also update the field that calculations.js actually reads
                      slumTDRPercentage: String(slum),
                    });
                  }}
                >
                  <SelectTrigger className="rounded-sm" data-testid="quick-tdr-split">
                    <SelectValue placeholder="Select ratio" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="70:30">70:30 (Amenity:Slum)</SelectItem>
                    <SelectItem value="50:50">50:50 (Amenity:Slum)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="q-amenityTdrPercent">Cost of Amenity TDR (% of Land RR) *</Label>
                <Input
                  id="q-amenityTdrPercent"
                  name="amenityTDRPercent"
                  type="number"
                  value={formData.amenityTDRPercent}
                  onChange={handleChange}
                  placeholder="50"
                  className={getInputClass("amenityTDRPercent")}
                  data-testid="quick-amenity-tdr-percent"
                />
                {hasFieldError("amenityTDRPercent") && <p className="text-xs text-red-500">Required</p>}
              </div>
              <div className="space-y-2">
                <Label>Slum TDR Cost Method</Label>
                <Select
                  value={formData.slumTDRMethod || "zone_drc"}
                  onValueChange={(value) => handleSelectChange("slumTDRMethod", value)}
                >
                  <SelectTrigger className="rounded-sm" data-testid="quick-slum-tdr-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rr_percent">Slum TDR (% of RR)</SelectItem>
                    <SelectItem value="zone_drc">Zone DRC (₹/sft)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.slumTDRMethod === "rr_percent" ? (
                <div className="space-y-2">
                  <Label htmlFor="q-slumTdrPercent">Slum TDR (% of Land RR) *</Label>
                  <Input
                    id="q-slumTdrPercent"
                    name="slumTDRRRPercent"
                    type="number"
                    value={formData.slumTDRRRPercent}
                    onChange={handleChange}
                    placeholder="40"
                    className={`rounded-sm ${(!formData.slumTDRRRPercent || parseFloat(formData.slumTDRRRPercent) === 0) ? 'border-red-400 bg-red-50' : ''}`}
                    data-testid="quick-slum-tdr-percent"
                    required
                  />
                  <p className="text-xs text-muted-foreground">Similar to Amenity TDR. Cost = Area × RR Rate × %</p>
                  {(!formData.slumTDRRRPercent || parseFloat(formData.slumTDRRRPercent) === 0) && (
                    <p className="text-xs text-red-500">Required for Slum TDR cost calculation</p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="q-slumTdrAmount">Slum TDR Zone DRC (₹/sft) *</Label>
                  <IndianNumberInput
                    id="q-slumTdrAmount"
                    name="slumTDRZoneDRC"
                    value={formData.slumTDRZoneDRC}
                    onChange={handleChange}
                    placeholder="2000"
                    className={`rounded-sm ${(!formData.slumTDRZoneDRC || parseFloat(formData.slumTDRZoneDRC) === 0) ? 'border-red-400 bg-red-50' : ''}`}
                    data-testid="quick-slum-tdr-amount"
                    required
                  />
                  <p className="text-xs text-muted-foreground">Fixed rate per sq.ft. Cost = Area × Rate</p>
                  {(!formData.slumTDRZoneDRC || parseFloat(formData.slumTDRZoneDRC) === 0) && (
                    <p className="text-xs text-red-500">Required for Slum TDR cost calculation</p>
                  )}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="q-fireFightingRate">Fire Fighting (₹/sq mtr)</Label>
                <Input
                  id="q-fireFightingRate"
                  name="fireFightingRate"
                  type="number"
                  value={formData.fireFightingRate || ""}
                  onChange={handleChange}
                  placeholder="250"
                  className="rounded-sm"
                  data-testid="quick-fire-fighting"
                />
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
